import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { ImpactRadar } from "@/components/impact-radar";
import { BadgeGrid, buildBadges } from "@/components/badge-grid";
import { JourneyTimeline, buildJourney } from "@/components/journey-timeline";
import { Flag, ShieldCheck, CheckCircle2, MessageSquareCheck, Eye, Network, Loader2, Landmark, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/impact/")({
  component: ImpactPage,
  head: () => ({
    meta: [
      { title: "Xamseen — Impact" },
      { name: "description", content: "Track the difference you make: issues reported, verified and resolved, solutions supported, people reached and projects joined." },
      { property: "og:title", content: "Your Impact on Xamseen" },
      { property: "og:description", content: "More than a score — see what your reports, verifications and solutions changed in the real world." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

type Impact = {
  reported: number; verified: number; resolved: number;
  solutions: number; reach: number; projects: number;
  spaces: number; followers: number; joinedAt: string;
  weekly: { label: string; count: number }[];
};

function ImpactPage() {
  const { user } = useAuth();
  const [data, setData] = useState<Impact | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      const uid = user.id;
      const [postsRes, verifs, sols, projs, spaces, followers, recent] = await Promise.all([
        supabase.from("posts").select("id, post_type, status, accepted_solution_id, view_count").eq("author_id", uid),
        supabase.from("reactions").select("id", { count: "exact", head: true }).eq("user_id", uid).eq("reaction", "verify" as any),
        supabase.from("comments").select("id", { count: "exact", head: true }).eq("author_id", uid).eq("is_solution", true),
        supabase.from("project_members").select("project_id", { count: "exact", head: true }).eq("user_id", uid),
        supabase.from("community_members").select("community_id", { count: "exact", head: true }).eq("user_id", uid),
        supabase.from("follows").select("follower_id", { count: "exact", head: true }).eq("following_id", uid),
        supabase.from("posts").select("created_at").eq("author_id", uid).gte("created_at", new Date(Date.now() - 56 * 86400000).toISOString()),
      ]);
      const list = (postsRes.data ?? []) as any[];
      const buckets: { label: string; count: number }[] = Array.from({ length: 8 }, (_, i) => ({
        label: `W${8 - i}`, count: 0,
      })).reverse();
      for (const r of (recent.data ?? []) as any[]) {
        const weeksAgo = Math.floor((Date.now() - new Date(r.created_at).getTime()) / (7 * 86400000));
        const idx = 7 - Math.min(7, weeksAgo);
        if (buckets[idx]) buckets[idx].count += 1;
      }
      setData({
        reported: list.filter(p => p.post_type === "issue" || p.post_type === "emergency").length,
        verified: verifs.count ?? 0,
        resolved: list.filter(p => p.status === "resolved" || p.accepted_solution_id).length,
        solutions: sols.count ?? 0,
        reach: list.reduce((a, p) => a + (p.view_count ?? 0), 0),
        projects: projs.count ?? 0,
        spaces: spaces.count ?? 0,
        followers: followers.count ?? 0,
        joinedAt: user.created_at,
        weekly: buckets,
      });
      setLoading(false);
    })();
  }, [user]);

  if (!user) {
    return (
      <AppShell>
        <div className="panel px-6 py-16 text-center">
          <h1 className="font-display text-xl font-bold">Impact is personal</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to see what your contributions changed.</p>
          <Link to="/auth" className="mt-5 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Sign in</Link>
        </div>
      </AppShell>
    );
  }

  if (loading || !data) {
    return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  }

  const axes = [
    { label: "Report", value: Math.min(100, data.reported * 12) },
    { label: "Verify", value: Math.min(100, data.verified * 10) },
    { label: "Answer", value: Math.min(100, data.solutions * 20) },
    { label: "Build", value: Math.min(100, data.projects * 25) },
    { label: "Resolve", value: Math.min(100, data.resolved * 20) },
    { label: "Reach", value: Math.min(100, Math.round(data.reach / 20)) },
  ];

  const max = Math.max(1, ...data.weekly.map(w => w.count));

  return (
    <AppShell
      rightRail={
        <div className="space-y-4 py-3">
          <div className="panel px-4 py-4">
            <h3 className="font-display text-[15px] font-semibold">What counts as impact</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Xamseen measures what moved: a report that got verified, an answer that became a solution, an issue that
              closed. Not how loud you were.
            </p>
          </div>
          <div className="panel px-4 py-4">
            <h3 className="mb-3 font-display text-[15px] font-semibold">My Journey</h3>
            <JourneyTimeline
              milestones={buildJourney({
                joinedAt: data.joinedAt,
                issuesReported: data.reported,
                verificationsGiven: data.verified,
                acceptedSolutions: data.solutions,
                projects: data.projects,
                issuesResolved: data.resolved,
                spaces: data.spaces,
              })}
            />
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <header>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Impact</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The trail you have left in your community — reported, verified, answered, resolved.
          </p>
        </header>

        <Link
          to="/impact/projects"
          className="panel flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-surface"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface">
            <Landmark className="h-4 w-4 text-tide" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Public works record</span>
            <span className="block text-xs text-muted-foreground">
              Projects and responses published by verified offices and organisations.
            </span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Link>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Tile icon={Flag} tone="text-ember" value={data.reported} label="Issues reported" />
          <Tile icon={ShieldCheck} tone="text-sun" value={data.verified} label="Reports verified" />
          <Tile icon={CheckCircle2} tone="text-moss" value={data.resolved} label="Issues resolved" />
          <Tile icon={MessageSquareCheck} tone="text-foreground" value={data.solutions} label="Solutions accepted" />
          <Tile icon={Eye} tone="text-tide" value={data.reach} label="People reached" />
          <Tile icon={Network} tone="text-moss" value={data.projects} label="Projects joined" />
        </div>

        <section className="panel panel-accent px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">Impact footprint</h2>
          <p className="text-xs text-muted-foreground">Where your energy goes across the Xamseen loop.</p>
          <ImpactRadar axes={axes} className="mt-2" />
        </section>

        <section className="panel px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">Last 8 weeks</h2>
          <div className="mt-4 flex h-32 items-end gap-2">
            {data.weekly.map((w, i) => (
              <div key={w.label + i} className="flex flex-1 flex-col items-center gap-2">
                <span
                  className="w-full rounded-t-md bg-foreground/70 transition-[height] duration-700"
                  style={{ height: `${Math.max(4, (w.count / max) * 100)}%` }}
                  title={`${w.count} contributions`}
                />
                <span className="text-[10px] text-muted-foreground">{w.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Badges</h2>
          <BadgeGrid
            badges={buildBadges({
              issuesReported: data.reported,
              verificationsGiven: data.verified,
              followers: data.followers,
              acceptedSolutions: data.solutions,
              spaces: data.spaces,
              issuesResolved: data.resolved,
            })}
          />
        </section>
      </div>
    </AppShell>
  );
}

function Tile({ icon: Icon, tone, value, label }: { icon: any; tone: string; value: number; label: string }) {
  return (
    <div className="panel px-4 py-4">
      <Icon className={cn("h-4 w-4", tone)} />
      <p className="mt-2 font-display text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
